import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, Switch, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, BellOutlined, CarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import CarsModal from '../components/CarsModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'client' | 'valet' | 'admin';
  isActive: boolean;
  createdAt: string;
  buildings?: { id: string; name: string }[];
  cars?: { id: string; licensePlate: string; make: string; model: string; color: string }[];
}

const UsersPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationUser, setNotificationUser] = useState<User | null>(null);
  const [notificationForm] = Form.useForm();
  const [form] = Form.useForm();
  const [isCarsModalOpen, setIsCarsModalOpen] = useState(false);
  const [carsUser, setCarsUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['users', pagination, debouncedSearch],
    queryFn: async () => {
      const offset = (pagination.current - 1) * pagination.pageSize;
      const params = new URLSearchParams();
      params.append('limit', String(pagination.pageSize));
      params.append('offset', String(offset));
      if (debouncedSearch) params.append('search', debouncedSearch);
      const response = await api.get(`/admin/users?${params}`);
      return response.data;
    }
  });

  const { data: buildingsData } = useQuery({
    queryKey: ['buildings'],
    queryFn: async () => {
      const response = await api.get('/admin/buildings');
      return response.data;
    }
  });

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data;
    }
  });

  const securitySettings = settingsData?.security || {};
  const minPasswordLength = parseInt(securitySettings.minPasswordLength) || 6;
  const passwordRequirements = [
    `minimum ${minPasswordLength} znaków`,
  ];
  if (securitySettings.requireUppercase === 'true') passwordRequirements.push('wielką literę');
  if (securitySettings.requireDigits === 'true') passwordRequirements.push('cyfrę');
  if (securitySettings.requireSpecialChars === 'true') passwordRequirements.push('znak specjalny');

  const createMutation = useMutation({
    mutationFn: (values: Partial<User>) => api.post('/admin/users', values),
    onSuccess: () => {
      message.success('Użytkownik utworzony');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Błąd tworzenia użytkownika'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<User> }) => api.put(`/admin/users/${id}`, values),
    onSuccess: () => {
      message.success('Użytkownik zaktualizowany');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      setEditingUser(null);
      form.resetFields();
    },
    onError: () => message.error('Błąd aktualizacji'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      message.success('Użytkownik usunięty');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => message.error('Błąd usuwania'),
  });

  const sendNotificationMutation = useMutation({
    mutationFn: ({ userId, type }: { userId: string; type: string }) => 
      api.post(`/admin/users/${userId}/test-notification`, { type }),
    onSuccess: () => {
      message.success('Powiadomienie testowe wysłane');
      setIsNotificationModalOpen(false);
      setNotificationUser(null);
      notificationForm.resetFields();
    },
    onError: () => message.error('Błąd wysyłania powiadomienia'),
  });
  const handleSendNotification = (user: User) => {
    setNotificationUser(user);
    setIsNotificationModalOpen(true);
  };

  const handleNotificationOk = () => {
    notificationForm.validateFields().then((values) => {
      if (notificationUser) {
        sendNotificationMutation.mutate({ userId: notificationUser.id, type: values.type });
      }
    });
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (editingUser) {
        updateMutation.mutate({ id: editingUser.id, values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    const { password, ...userWithoutPassword } = user as User & { password?: string };
    const buildingIds = user.buildings?.map(b => b.id) || [];
    form.setFieldsValue({ ...userWithoutPassword, buildingIds });
    setIsModalOpen(true);
  };

  const handleManageCars = (user: User) => {
    setCarsUser(user);
    setIsCarsModalOpen(true);
  };

  const columns = [
    { title: 'Imię', dataIndex: 'firstName', key: 'firstName' },
    { title: 'Nazwisko', dataIndex: 'lastName', key: 'lastName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Rola',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colors: Record<string, string> = { client: 'blue', valet: 'green', admin: 'red' };
        return <Tag color={colors[role]}>{role}</Tag>;
      },
    },
    {
      title: 'Budynki',
      key: 'buildings',
      render: (_: any, record: User) => (
        <span>{record.buildings?.map(b => b.name).join(', ') || '-'}</span>
      ),
    },
    {
      title: 'Aktywny',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: User) => (
        <Switch checked={isActive} onChange={(checked) => updateMutation.mutate({ id: record.id, values: { isActive: checked } })} />
      ),
    },
    {
      title: 'Akcje',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button icon={<BellOutlined />} size="small" onClick={() => handleSendNotification(record)} title="Wyślij powiadomienie testowe" />
          <Button icon={<CarOutlined />} size="small" onClick={() => handleManageCars(record)} title="Zarządzaj samochodami" />
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Czy na pewno usunąć?" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Użytkownicy</h1>
        <Space>
          <Input
            placeholder="Szukaj..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingUser(null); form.resetFields(); setIsModalOpen(true); }}>
            Dodaj użytkownika
          </Button>
        </Space>
      </div>
      <Table 
        dataSource={data?.users || []} 
        columns={columns} 
        rowKey="id" 
        loading={isLoading}
        pagination={{
          ...pagination,
          total: data?.total || 0,
          showSizeChanger: true,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
      />
      <Modal title={editingUser ? 'Edytuj użytkownika' : 'Dodaj użytkownika'} open={isModalOpen} onOk={handleOk} onCancel={() => { setIsModalOpen(false); setEditingUser(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="firstName" label="Imię" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Nazwisko" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Rola" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="client">Klient</Select.Option>
              <Select.Option value="valet">Lokaj</Select.Option>
              <Select.Option value="admin">Admin</Select.Option>
            </Select>
          </Form.Item>
          {editingUser && (
            <Form.Item name="buildingIds" label="Budynki">
              <Select mode="multiple" placeholder="Wybierz budynki">
                {buildingsData?.buildings?.map((b: any) => (
                  <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {editingUser ? (
            <Form.Item name="password" label="Nowe hasło (opcjonalne)" help={`Wymagania: ${passwordRequirements.join(', ')}`}>
              <Input.Password placeholder="Zostaw puste aby nie zmieniać" />
            </Form.Item>
          ) : (
            <Form.Item name="password" label="Hasło" rules={[{ required: true }]} help={`Wymagania: ${passwordRequirements.join(', ')}`}>
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal 
        title="Wyślij powiadomienie testowe" 
        open={isNotificationModalOpen} 
        onOk={handleNotificationOk} 
        onCancel={() => { setIsNotificationModalOpen(false); setNotificationUser(null); notificationForm.resetFields(); }}
        confirmLoading={sendNotificationMutation.isPending}
      >
        <p>Użytkownik: <strong>{notificationUser?.firstName} {notificationUser?.lastName}</strong></p>
        <Form form={notificationForm} layout="vertical">
          <Form.Item name="type" label="Typ powiadomienia" rules={[{ required: true, message: 'Wybierz typ powiadomienia' }]}>
            <Select placeholder="Wybierz typ">
              <Select.Option value="accepted">Żądanie przyjęte</Select.Option>
              <Select.Option value="delivered">Samochód dostarczony</Select.Option>
              <Select.Option value="completed">Samochód odebrany</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <CarsModal
        open={isCarsModalOpen}
        onClose={() => { setIsCarsModalOpen(false); setCarsUser(null); }}
        userId={carsUser?.id || ''}
        userName={carsUser ? `${carsUser.firstName} ${carsUser.lastName}` : ''}
      />
    </div>
  );
};

export default UsersPage;
